import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { MessageCircle, Copy, ExternalLink, Check, RefreshCw, Unlink } from 'lucide-react';

interface TelegramLinkFlowProps {
  telegramChatId: string | null;
  onLinked: () => void;
}

export function TelegramLinkFlow({ telegramChatId, onLinked }: TelegramLinkFlowProps) {
  const { user } = useAuth();
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  const isLinked = !!telegramChatId;

  // Generate 6-digit verification code
  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleGenerateCode = async () => {
    if (!user) return;

    setIsGenerating(true);
    try {
      const code = generateCode();
      const expires = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes

      const { error } = await supabase
        .from('profiles')
        .update({
          telegram_verification_code: code,
          telegram_verification_expires: expires.toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      setVerificationCode(code);
      setExpiresAt(expires);
      toast.success('Código generado. Tienes 15 minutos para usarlo.');
    } catch (error) {
      console.error('Error generating code:', error);
      toast.error('Error al generar el código');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyCode = async () => {
    if (!verificationCode) return;

    try {
      await navigator.clipboard.writeText(`/start ${verificationCode}`);
      setCopied(true);
      toast.success('Comando copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Error al copiar');
    }
  };

  const handleUnlink = async () => {
    if (!user) return;

    setIsUnlinking(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          telegram_chat_id: null,
          deadline_notifications_telegram: false,
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Telegram desvinculado correctamente');
      onLinked();
    } catch (error) {
      console.error('Error unlinking Telegram:', error);
      toast.error('Error al desvincular Telegram');
    } finally {
      setIsUnlinking(false);
    }
  };

  // Poll for linking status when code is active
  useEffect(() => {
    if (!verificationCode || !user) return;

    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('telegram_chat_id')
        .eq('id', user.id)
        .single();

      if (data?.telegram_chat_id) {
        setVerificationCode(null);
        toast.success('¡Telegram vinculado correctamente!');
        onLinked();
        clearInterval(interval);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [verificationCode, user, onLinked]);

  // Check expiration
  useEffect(() => {
    if (!expiresAt) return;

    const checkExpiry = () => {
      if (new Date() > expiresAt) {
        setVerificationCode(null);
        setExpiresAt(null);
      }
    };

    const interval = setInterval(checkExpiry, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (isLinked) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="p-2 bg-green-100 dark:bg-green-800 rounded-full">
            <MessageCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-green-800 dark:text-green-200">
              Telegram vinculado
            </p>
            <p className="text-sm text-green-600 dark:text-green-400">
              Recibirás notificaciones de plazos en Telegram
            </p>
          </div>
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            Activo
          </Badge>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleUnlink}
          disabled={isUnlinking}
          className="text-destructive hover:text-destructive"
        >
          <Unlink className="h-4 w-4 mr-2" />
          {isUnlinking ? 'Desvinculando...' : 'Desvincular Telegram'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-muted/50 rounded-lg border">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
            <MessageCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="font-medium">Vincular Telegram</p>
            <p className="text-sm text-muted-foreground mt-1">
              Recibe recordatorios de plazos directamente en tu Telegram.
            </p>
          </div>
        </div>
      </div>

      {!verificationCode ? (
        <div className="space-y-3">
          <Button onClick={handleGenerateCode} disabled={isGenerating} className="w-full">
            <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
            {isGenerating ? 'Generando código...' : 'Generar código de vinculación'}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <Label className="text-sm font-medium">Tu código de verificación</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                value={verificationCode}
                readOnly
                className="font-mono text-xl text-center tracking-widest"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyCode}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            {expiresAt && (
              <p className="text-xs text-muted-foreground mt-2">
                Expira en {Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 60000))} minutos
              </p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Instrucciones:</p>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              <li>Abre Telegram y busca <strong>@MunilexPlazosBot</strong></li>
              <li>
                Envía el comando:{' '}
                <code className="bg-muted px-2 py-0.5 rounded text-xs">
                  /start {verificationCode}
                </code>
              </li>
              <li>¡Listo! El bot confirmará la vinculación</li>
            </ol>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCopyCode}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copiar comando
            </Button>
            <Button
              variant="default"
              className="flex-1"
              asChild
            >
              <a
                href="https://t.me/MunilexPlazosBot"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir Telegram
              </a>
            </Button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleGenerateCode}
            disabled={isGenerating}
            className="w-full"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
            Generar nuevo código
          </Button>
        </div>
      )}
    </div>
  );
}
